import React from 'react';
import '../../styles/SettingsPage.css';
import { API_BASE_URL, getAuthHeaders } from '../../utils/api';
import IngestionCRUD from '../../components/IngestionCRUD';
import ViewsCRUD from '../../components/ViewsCRUD';
import { SettingsSystemTab } from './SettingsSystemTab';
import { SettingsUserTab } from './SettingsUserTab';
import { SettingsDataInputTab } from './SettingsDataInputTab';
import { SettingsDataOutputTab } from './SettingsDataOutputTab';

export function SettingsPageView(p) {
    const {
        activeTab,
        dataEntries,
        dataProcessingFields,
        dataSettings,
        handleDeleteDataEntry,
        handleDiscardChanges,
        handleUpdateDatabase,
        handleUpdateProcessingDatabase,
        initialTab,
        message,
        saving,
        setActiveTab,
        setMessage,
        setSaving,
        setShowCreateEntryTableConfirm,
        setShowCreateProcessingTableConfirm,
        setShowDeleteConfirm,
        setShowDiscardConfirm,
        setShowUpdateConfirm,
        showCreateEntryTableConfirm,
        showCreateProcessingTableConfirm,
        showDeleteConfirm,
        showDiscardConfirm,
        showUpdateConfirm,
    } = p;

    return (
        <div className="settings-page">
            <div id="settings-message">
                {message.text && (
                    <div className={`alert alert-${message.type}`}>
                        <i className={`fas fa-${message.type === 'success' ? 'check-circle' : 'exclamation-circle'}`}></i>
                        {message.text}
                    </div>
                )}
            </div>

            {/* Tab Navigation - only show if initialTab is null or undefined (allows switching between tabs) */}
            {(initialTab === null || initialTab === undefined) && (
                <div className="settings-tabs">
                    <button
                        className={`settings-tab ${activeTab === 'system' ? 'active' : ''}`}
                        onClick={() => setActiveTab('system')}
                    >
                        <i className="fas fa-cog"></i> System Settings
                    </button>
                    <button
                        className={`settings-tab ${activeTab === 'user' ? 'active' : ''}`}
                        onClick={() => setActiveTab('user')}
                    >
                        <i className="fas fa-user"></i> User Settings
                    </button>
                    <button
                        className={`settings-tab ${activeTab === 'ingestion' ? 'active' : ''}`}
                        onClick={() => setActiveTab('ingestion')}
                    >
                        <i className="fas fa-cloud-upload-alt"></i> Ingestion
                    </button>
                    <button
                        className={`settings-tab ${activeTab === 'views' ? 'active' : ''}`}
                        onClick={() => setActiveTab('views')}
                    >
                        <i className="fas fa-filter"></i> Views
                    </button>
                </div>
            )}

            {/* Tab Content */}

            {activeTab === 'system' && (
                <SettingsSystemTab p={p} />
            )}

            {activeTab === 'user' && (
                <SettingsUserTab p={p} />
            )}

            {activeTab === 'data-input' && (
                <SettingsDataInputTab p={p} />
            )}

            {activeTab === 'data-output' && (
                <SettingsDataOutputTab p={p} />
            )}



            {/* Update Database Confirmation Modal */}
            {showUpdateConfirm && (
                <div className="modal show">
                    <div className="modal-content">
                        <div className="modal-header">
                            <h2 className="modal-title">Confirm Update Database</h2>
                            <span className="close" onClick={() => setShowUpdateConfirm(false)}>&times;</span>
                        </div>
                        <div className="modal-body">
                            <p>This will commit all pending changes to the database. If any schema changes are detected,
                                the schema version will be incremented from the database baseline.</p>
                            {activeTab === 'data-output' && (
                                <p style={{color: '#f59e0b', fontWeight: 'bold', marginTop: '10px'}}>
                                    <i className="fas fa-exclamation-triangle"></i> If schema changes are detected,
                                    older data will be moved to a historical table (YYMMDD_{'{schema}'}) and a new
                                    processing table will be created.
                                </p>
                            )}
                            <p><strong>Continue?</strong></p>
                        </div>
                        <div className="modal-footer">
                            <button
                                type="button"
                                className="btn btn-secondary"
                                onClick={() => setShowUpdateConfirm(false)}
                            >
                                Cancel
                            </button>
                            <button
                                type="button"
                                className="btn btn-primary"
                                onClick={() => {
                                    if (activeTab === 'data-output') {
                                        handleUpdateProcessingDatabase();
                                    } else {
                                        handleUpdateDatabase();
                                    }
                                }}
                            >
                                <i className="fas fa-database"></i> Update Database
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Ingestion Tab */}
            {activeTab === 'ingestion' && (
                <div className="settings-tab-content">
                    <IngestionCRUD />
                </div>
            )}

            {/* Views Tab */}
            {activeTab === 'views' && (
                <div className="settings-tab-content">
                    <ViewsCRUD />
                </div>
            )}

            {/* Discard Changes Confirmation Modal */}
            {showDiscardConfirm && (
                <div className="modal show">
                    <div className="modal-content">
                        <div className="modal-header">
                            <h2 className="modal-title">Confirm Discard Changes</h2>
                            <span className="close" onClick={() => setShowDiscardConfirm(false)}>&times;</span>
                        </div>
                        <div className="modal-body">
                            <p>Are you sure you want to discard all pending changes?</p>
                        </div>
                        <div className="modal-footer">
                            <button
                                type="button"
                                className="btn btn-secondary"
                                onClick={() => setShowDiscardConfirm(false)}
                            >
                                Cancel
                            </button>
                            <button
                                type="button"
                                className="btn btn-danger"
                                onClick={handleDiscardChanges}
                            >
                                <i className="fas fa-undo"></i> Discard Changes
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Delete Data Entry Confirmation Modal */}
            {showDeleteConfirm && (
                <div className="modal show">
                    <div className="modal-content">
                        <div className="modal-header">
                            <h2 className="modal-title">Confirm Delete</h2>
                            <span className="close" onClick={() => setShowDeleteConfirm(false)}>&times;</span>
                        </div>
                        <div className="modal-body">
                            <p>Are you sure you want to delete this data entry type? Changes will be local until you
                                click "Update Database".</p>
                        </div>
                        <div className="modal-footer">
                            <button
                                type="button"
                                className="btn btn-secondary"
                                onClick={() => setShowDeleteConfirm(false)}
                            >
                                Cancel
                            </button>
                            <button
                                type="button"
                                className="btn btn-danger"
                                onClick={handleDeleteDataEntry}
                            >
                                <i className="fas fa-trash"></i> Delete
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Create Processing Table Confirmation Modal */}
            {showCreateProcessingTableConfirm && (
                <div className="modal show">
                    <div className="modal-content">
                        <div className="modal-header">
                            <h2 className="modal-title">Create Processing Table</h2>
                            <span className="close"
                                  onClick={() => setShowCreateProcessingTableConfirm(false)}>&times;</span>
                        </div>
                        <div className="modal-body">
                            <p>This will create
                                the <code>data_processed_{dataSettings.activeSuffix || 'rsmg_sales'}</code> table based
                                on your current field definitions.</p>
                            <p style={{marginTop: '10px', color: '#666', fontSize: '0.9em'}}>
                                <i className="fas fa-info-circle"></i> Historical data will be preserved if the table
                                already exists.
                            </p>
                        </div>
                        <div className="modal-footer">
                            <button
                                type="button"
                                className="btn btn-secondary"
                                onClick={() => setShowCreateProcessingTableConfirm(false)}
                            >
                                Cancel
                            </button>
                            <button
                                type="button"
                                className="btn btn-primary"
                                onClick={async () => {
                                    setShowCreateProcessingTableConfirm(false);
                                    setSaving(true);

                                    try {
                                        // Get current suffix
                                        const currentSuffix = dataSettings.activeSuffix || 'rsmg_sales';

                                        // Get schema version from stored value or calculate from current fields
                                        let schemaVersion = window._pendingProcessingSchemaVersion;
                                        if (!schemaVersion) {
                                            const maxVersion = dataProcessingFields.length > 0
                                                ? Math.max(...dataProcessingFields.map(f => f.schema_version || 1))
                                                : 1;
                                            schemaVersion = maxVersion;
                                        }

                                        const headers = await getAuthHeaders();
                                        const response = await fetch(`${API_BASE_URL}/casc/v1/api/data-processing-fields/create-table`, {
                                            method: 'POST',
                                            headers,
                                            body: JSON.stringify({
                                                suffix: currentSuffix,
                                                schema_version: schemaVersion
                                            })
                                        });

                                        const data = await response.json();

                                        if (data.success) {
                                            setMessage({
                                                type: 'success',
                                                text: `Processing table created successfully: ${data.message}`
                                            });
                                            setTimeout(() => setMessage({type: '', text: ''}), 5000);
                                            // Clear the stored schema version
                                            delete window._pendingProcessingSchemaVersion;
                                        } else {
                                            setMessage({
                                                type: 'error',
                                                text: `Failed to create processing table: ${data.error || 'Unknown error'}`
                                            });
                                            setTimeout(() => setMessage({type: '', text: ''}), 10000);
                                        }
                                    } catch (error) {
                                        console.error('Error creating processing table:', error);
                                        setMessage({
                                            type: 'error',
                                            text: `Error creating processing table: ${error.message}`
                                        });
                                        setTimeout(() => setMessage({type: '', text: ''}), 10000);
                                    } finally {
                                        setSaving(false);
                                    }
                                }}
                                disabled={saving}
                            >
                                <i className="fas fa-database"></i> Create Processing Table
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Create Entry Table Confirmation Modal */}
            {showCreateEntryTableConfirm && (
                <div className="modal show">
                    <div className="modal-content">
                        <div className="modal-header">
                            <h2 className="modal-title">Create Entry Table</h2>
                            <span className="close" onClick={() => setShowCreateEntryTableConfirm(false)}>&times;</span>
                        </div>
                        <div className="modal-body">
                            <p>This will create
                                the <code>data_entered_{dataSettings.activeSuffix || 'rsmg_sales'}</code> table based on
                                your current field definitions.</p>
                            <p style={{marginTop: '10px', color: '#666', fontSize: '0.9em'}}>
                                <i className="fas fa-info-circle"></i> Historical data will be preserved if the table
                                already exists.
                            </p>
                        </div>
                        <div className="modal-footer">
                            <button
                                type="button"
                                className="btn btn-secondary"
                                onClick={() => setShowCreateEntryTableConfirm(false)}
                            >
                                Cancel
                            </button>
                            <button
                                type="button"
                                className="btn btn-primary"
                                onClick={async () => {
                                    setShowCreateEntryTableConfirm(false);
                                    setSaving(true);

                                    try {
                                        // Get current suffix
                                        const currentSuffix = dataSettings.activeSuffix || 'rsmg_sales';

                                        // Get schema version from stored value or calculate from current entries
                                        let schemaVersion = window._pendingEntrySchemaVersion;
                                        if (!schemaVersion) {
                                            const maxVersion = dataEntries.length > 0
                                                ? Math.max(...dataEntries.map(e => e.schema_version || 1))
                                                : 1;
                                            schemaVersion = maxVersion;
                                        }

                                        const headers = await getAuthHeaders();
                                        const response = await fetch(`${API_BASE_URL}/casc/v1/api/data-entries/create-table`, {
                                            method: 'POST',
                                            headers,
                                            body: JSON.stringify({
                                                suffix: currentSuffix,
                                                schema_version: schemaVersion
                                            })
                                        });

                                        const data = await response.json();

                                        if (data.success) {
                                            setMessage({
                                                type: 'success',
                                                text: `Entry table created successfully: ${data.message}`
                                            });
                                            setTimeout(() => setMessage({type: '', text: ''}), 5000);
                                            // Clear the stored schema version
                                            delete window._pendingEntrySchemaVersion;
                                        } else {
                                            setMessage({
                                                type: 'error',
                                                text: `Failed to create entry table: ${data.error || 'Unknown error'}`
                                            });
                                            setTimeout(() => setMessage({type: '', text: ''}), 10000);
                                        }
                                    } catch (error) {
                                        console.error('Error creating entry table:', error);
                                        setMessage({
                                            type: 'error',
                                            text: `Error creating entry table: ${error.message}`
                                        });
                                        setTimeout(() => setMessage({type: '', text: ''}), 10000);
                                    } finally {
                                        setSaving(false);
                                    }
                                }}
                                disabled={saving}
                            >
                                <i className="fas fa-database"></i> Create Entry Table
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
